import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Lightbulb, Hammer, StickyNote, Plus, Trash2, Check, Circle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ETIQUETAS = {
  mantenimiento: { label: '#mantenimiento', icon: Wrench, color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/40' },
  idea: { label: '#idea', icon: Lightbulb, color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/40' },
  trabajo: { label: '#trabajo', icon: Hammer, color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40' },
  otro: { label: '#otro', icon: StickyNote, color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/40' },
};

export const NotasTab = ({ headers }) => {
  const [notas, setNotas] = useState([]);
  const [texto, setTexto] = useState('');
  const [etiqueta, setEtiqueta] = useState('mantenimiento');
  const [filtro, setFiltro] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/familia/notas`, { headers });
      setNotas(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const addNota = async () => {
    if (!texto.trim()) return;
    try {
      await axios.post(`${API}/familia/notas`, { texto: texto.trim(), etiqueta }, { headers });
      setTexto('');
      load();
    } catch (e) { toast.error('No se pudo guardar la nota'); }
  };

  const toggleHecho = async (nota) => {
    try {
      await axios.put(`${API}/familia/notas/${nota.id}`, { hecho: !nota.hecho }, { headers });
      load();
    } catch (e) { console.error(e); }
  };

  const deleteNota = async (id) => {
    if (!window.confirm('¿Eliminar nota?')) return;
    try {
      await axios.delete(`${API}/familia/notas/${id}`, { headers });
      load();
    } catch (e) { console.error(e); }
  };

  const filtradas = filtro ? notas.filter(n => n.etiqueta === filtro) : notas;

  return (
    <div className="px-4 pb-24" data-testid="notas-tab">
      {/* Form de añadir */}
      <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3 mb-4">
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          placeholder="Apunta un trabajo, idea o cosa por arreglar..."
          rows={2}
          className="w-full bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none resize-none"
          data-testid="nota-input"
        />
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(ETIQUETAS).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const active = etiqueta === key;
              return (
                <button
                  key={key}
                  onClick={() => setEtiqueta(key)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border transition-all ${
                    active ? `${cfg.bg} ${cfg.border} ${cfg.color}` : 'border-gray-700 text-gray-500 hover:text-gray-300'
                  }`}
                  data-testid={`etiqueta-${key}`}
                >
                  <Icon className="w-3 h-3" /> {cfg.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={addNota}
            disabled={!texto.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-semibold disabled:opacity-40"
            data-testid="nota-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Añadir
          </button>
        </div>
      </div>

      {/* Filtros */}
      {notas.length > 0 && (
        <div className="flex gap-1.5 mb-3 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setFiltro(null)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${
              !filtro ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'border-gray-700 text-gray-500'
            }`}
            data-testid="filtro-todas"
          >
            Todas ({notas.length})
          </button>
          {Object.entries(ETIQUETAS).map(([key, cfg]) => {
            const count = notas.filter(n => n.etiqueta === key).length;
            if (count === 0) return null;
            return (
              <button
                key={key}
                onClick={() => setFiltro(filtro === key ? null : key)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${
                  filtro === key ? `${cfg.bg} ${cfg.border} ${cfg.color}` : 'border-gray-700 text-gray-500'
                }`}
                data-testid={`filtro-${key}`}
              >
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <p className="text-gray-500 text-sm text-center py-8">Cargando...</p>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-10">
          <StickyNote className="w-10 h-10 text-gray-700 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No hay notas todavía. Apunta la primera.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtradas.map(nota => {
            const cfg = ETIQUETAS[nota.etiqueta] || ETIQUETAS.otro;
            const Icon = cfg.icon;
            return (
              <div
                key={nota.id}
                className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border ${
                  nota.hecho ? 'bg-gray-900/30 border-gray-800/50 opacity-60' : 'bg-gray-900/60 border-gray-800'
                }`}
                style={{ borderLeftWidth: 3, borderLeftColor: nota.hecho ? '#374151' : undefined }}
                data-testid={`nota-${nota.id}`}
              >
                <button onClick={() => toggleHecho(nota)} className="flex-shrink-0 mt-0.5" data-testid={`nota-toggle-${nota.id}`}>
                  {nota.hecho ? (
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </span>
                  ) : (
                    <Circle className="w-5 h-5 text-gray-600 hover:text-amber-400 transition-colors" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${nota.hecho ? 'text-gray-500 line-through' : 'text-white'}`}>{nota.texto}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${cfg.bg} ${cfg.color}`}>
                      <Icon className="w-2.5 h-2.5" /> {cfg.label}
                    </span>
                    {nota.created_by_nombre && (
                      <span className="text-[9px] text-gray-500">por {nota.created_by_nombre}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => deleteNota(nota.id)} className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`nota-delete-${nota.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
